import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link, useLocation, Navigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';

const FALLBACK_TTL_MS = 15 * 60 * 1000;

interface VerifyEmailLocationState {
  email?: string;
  verificationExpiresAt?: string;
}

function formatMmSs(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface VerifyPageState {
  autoLoading: boolean;
  tokenError: string;
  error: string;
  deadlineMs: number;
  code: string;
  fallbackEmail: string;
  loading: boolean;
  resendLoading: boolean;
  resendInfo: string;
}

interface VerifyPageInitArg {
  tokenFromUrl: string;
  navState: VerifyEmailLocationState | null;
}

function initVerifyPageState(arg: VerifyPageInitArg): VerifyPageState {
  let deadlineMs = Date.now() + FALLBACK_TTL_MS;
  const iso = arg.navState?.verificationExpiresAt;
  if (iso) {
    const t = Date.parse(iso);
    if (!Number.isNaN(t)) {
      deadlineMs = t;
    }
  }
  return {
    autoLoading: Boolean(arg.tokenFromUrl),
    tokenError: '',
    error: '',
    deadlineMs,
    code: '',
    fallbackEmail: '',
    loading: false,
    resendLoading: false,
    resendInfo: '',
  };
}

type VerifyPageAction =
  | { type: 'token-mutate-start' }
  | { type: 'token-mutate-error'; message: string }
  | { type: 'token-mutate-settled' }
  | { type: 'set-auto-loading'; value: boolean }
  | { type: 'resend-prep' }
  | { type: 'before-resend' }
  | { type: 'resend-success'; message: string; deadlineMs?: number }
  | { type: 'resend-fail'; message: string }
  | { type: 'resend-done' }
  | { type: 'submit-clear-error' }
  | { type: 'set-error'; message: string }
  | { type: 'before-submit-code' }
  | { type: 'submit-code-done' }
  | { type: 'set-code'; value: string }
  | { type: 'set-fallback-email'; value: string };

function verifyPageReducer(state: VerifyPageState, action: VerifyPageAction): VerifyPageState {
  switch (action.type) {
    case 'token-mutate-start':
      return { ...state, autoLoading: true, tokenError: '', error: '' };
    case 'token-mutate-error':
      return { ...state, tokenError: action.message };
    case 'token-mutate-settled':
      return { ...state, autoLoading: false };
    case 'set-auto-loading':
      return { ...state, autoLoading: action.value };
    case 'resend-prep':
      return { ...state, tokenError: '', error: '', resendInfo: '' };
    case 'before-resend':
      return { ...state, resendLoading: true, resendInfo: '', error: '' };
    case 'resend-success':
      return {
        ...state,
        resendInfo: action.message,
        deadlineMs: typeof action.deadlineMs === 'number' ? action.deadlineMs : state.deadlineMs,
      };
    case 'resend-fail':
      return { ...state, error: action.message };
    case 'resend-done':
      return { ...state, resendLoading: false };
    case 'submit-clear-error':
      return { ...state, error: '' };
    case 'set-error':
      return { ...state, error: action.message };
    case 'before-submit-code':
      return { ...state, loading: true, error: '' };
    case 'submit-code-done':
      return { ...state, loading: false };
    case 'set-code':
      return { ...state, code: action.value };
    case 'set-fallback-email':
      return { ...state, fallbackEmail: action.value };
    default:
      return state;
  }
}

function useCountdownRemainingSec(deadlineMs: number): number {
  const [sec, setSec] = useState(() => Math.max(0, Math.floor((deadlineMs - Date.now()) / 1000)));

  useEffect(() => {
    setSec(Math.max(0, Math.floor((deadlineMs - Date.now()) / 1000)));
    const id = window.setInterval(() => {
      setSec(Math.max(0, Math.floor((deadlineMs - Date.now()) / 1000)));
    }, 1000);
    return () => window.clearInterval(id);
  }, [deadlineMs]);

  return sec;
}

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const setAuth = useAuthStore((s) => s.setAuth);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const navState = (location.state as VerifyEmailLocationState | null) ?? null;
  const emailFromQuery = searchParams.get('email')?.trim().toLowerCase() ?? '';
  const emailFromState = navState?.email?.trim().toLowerCase() ?? '';
  const knownEmail = emailFromState || emailFromQuery;

  const tokenFromUrl = searchParams.get('token')?.trim() ?? '';

  const [verifyState, dispatch] = useReducer(
    verifyPageReducer,
    { tokenFromUrl, navState },
    initVerifyPageState,
  );

  const remainingSec = useCountdownRemainingSec(verifyState.deadlineMs);
  const effectiveEmail = knownEmail || verifyState.fallbackEmail.trim().toLowerCase();
  const { autoLoading, tokenError, error } = verifyState;
  const showEmailField = !knownEmail && Boolean(tokenFromUrl) && Boolean(tokenError) && !autoLoading;

  const finishAuth = useCallback(
    (data: {
      user: { id: string; email: string; name: string | null };
      tokens: { accessToken: string };
    }) => {
      setAuth(
        { ...data.user, name: data.user.name ?? '' },
        data.tokens,
      );
      queryClient.clear();
      navigate('/dashboard', { replace: true });
    },
    [navigate, queryClient, setAuth],
  );

  /** Не кладём `finishAuth` в колбэки мутации через замыкание — держим ref для свежего navigate/setAuth. */
  const finishAuthRef = useRef(finishAuth);
  finishAuthRef.current = finishAuth;

  const verifyByTokenMutation = useMutation({
    mutationFn: async (token: string) => {
      return (await api.post('/auth/verify-email', {
        token,
      })) as { user: { id: string; email: string; name: string | null }; tokens: unknown };
    },
    onMutate: () => {
      dispatch({ type: 'token-mutate-start' });
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries();
      finishAuthRef.current({
        user: data.user,
        tokens: data.tokens as { accessToken: string },
      });
    },
    onError: (err: unknown) => {
      dispatch({
        type: 'token-mutate-error',
        message:
          err instanceof Error
            ? err.message
            : 'Ссылка недействительна или истекла. Введите код из письма.',
      });
    },
    onSettled: () => {
      dispatch({ type: 'token-mutate-settled' });
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      dispatch({ type: 'set-auto-loading', value: false });
      return;
    }
    if (!tokenFromUrl) {
      dispatch({ type: 'set-auto-loading', value: false });
      return;
    }
    verifyByTokenMutation.mutate(tokenFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только token/isAuth; mutate стабилен в RQ
  }, [tokenFromUrl, isAuthenticated]);

  async function handleResend() {
    dispatch({ type: 'resend-prep' });
    if (!effectiveEmail) {
      dispatch({ type: 'set-error', message: 'Укажите email.' });
      return;
    }
    dispatch({ type: 'before-resend' });
    try {
      const data = (await api.post('/auth/resend-verification', {
        email: effectiveEmail,
      })) as { message?: string; verificationExpiresAt?: string };
      let nextDeadline: number | undefined;
      if (typeof data.verificationExpiresAt === 'string') {
        const t = Date.parse(data.verificationExpiresAt);
        if (!Number.isNaN(t)) {
          nextDeadline = t;
        }
      }
      dispatch({
        type: 'resend-success',
        message:
          typeof data.message === 'string' ? data.message : 'Если адрес зарегистрирован, проверьте почту.',
        deadlineMs: nextDeadline,
      });
    } catch (err) {
      dispatch({
        type: 'resend-fail',
        message: err instanceof Error ? err.message : 'Не удалось отправить письмо.',
      });
    } finally {
      dispatch({ type: 'resend-done' });
    }
  }

  async function handleSubmitCode(e: React.FormEvent) {
    e.preventDefault();
    dispatch({ type: 'submit-clear-error' });
    const codeNorm = verifyState.code.replace(/\s/g, '');
    if (!effectiveEmail) {
      dispatch({ type: 'set-error', message: 'Укажите email.' });
      return;
    }
    if (!/^\d{6}$/.test(codeNorm)) {
      dispatch({ type: 'set-error', message: 'Введите 6 цифр кода из письма.' });
      return;
    }
    dispatch({ type: 'before-submit-code' });
    try {
      const data = (await api.post('/auth/verify-email', {
        email: effectiveEmail,
        code: codeNorm,
      })) as { user: { id: string; email: string; name: string | null }; tokens: unknown };
      finishAuth({
        user: data.user,
        tokens: data.tokens as { accessToken: string },
      });
    } catch (err) {
      dispatch({
        type: 'set-error',
        message:
          err instanceof Error
            ? err.message
            : 'Не удалось подтвердить. Проверьте код и срок действия.',
      });
    } finally {
      dispatch({ type: 'submit-code-done' });
    }
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!knownEmail && !tokenFromUrl) {
    return <Navigate to="/login" replace />;
  }

  const showCodeForm = !autoLoading && (!tokenFromUrl || tokenError);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted p-4">
      <Card className="w-full max-w-md py-10">
        <CardHeader className="text-center px-12">
          <CardTitle className="text-2xl font-bold">Подтвердите email</CardTitle>
          <CardDescription className="text-balance">
            {autoLoading
              ? 'Проверяем ссылку из письма…'
              : knownEmail
                ? `Введите 6-значный код из письма, отправленного на ${knownEmail}.`
                : 'Введите код из письма или запросите новое письмо.'}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmitCode} className="flex flex-col gap-7">
          <CardContent className="flex flex-col gap-4 px-12">
            {knownEmail && showCodeForm && (
              <p className="text-center text-sm text-muted-foreground">
                {remainingSec > 0 ? (
                  <>
                    Код действителен ещё{' '}
                    <span className="font-medium tabular-nums text-foreground">{formatMmSs(remainingSec)}</span>
                  </>
                ) : (
                  <span>Срок действия кода истёк — нажмите «Отправить письмо снова».</span>
                )}
              </p>
            )}
            {tokenError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{tokenError}</div>
            )}
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
            )}
            {verifyState.resendInfo && (
              <div className="rounded-md bg-muted/60 p-3 text-sm text-foreground">{verifyState.resendInfo}</div>
            )}
            {showEmailField && (
              <div className="flex flex-col gap-2">
                <label htmlFor="verify-email-fallback" className="text-sm font-medium">
                  Email
                </label>
                <Input
                  id="verify-email-fallback"
                  type="email"
                  placeholder="you@example.com"
                  value={verifyState.fallbackEmail}
                  onChange={(e) => dispatch({ type: 'set-fallback-email', value: e.target.value })}
                  autoComplete="email"
                />
              </div>
            )}
            {showCodeForm && (
              <div className="flex flex-col gap-2">
                <label htmlFor="verify-code" className="text-sm font-medium">
                  Код из письма
                </label>
                <Input
                  id="verify-code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={verifyState.code}
                  onChange={(e) =>
                    dispatch({
                      type: 'set-code',
                      value: e.target.value.replace(/\D/g, '').slice(0, 6),
                    })}
                  autoComplete="one-time-code"
                />
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-4 px-12">
            {showCodeForm && (
              <>
                <Button type="submit" className="w-full" disabled={verifyState.loading || !effectiveEmail}>
                  {verifyState.loading ? 'Проверяем…' : 'Подтвердить'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  disabled={verifyState.resendLoading || !effectiveEmail}
                  onClick={handleResend}
                >
                  {verifyState.resendLoading ? 'Отправляем…' : 'Отправить письмо снова'}
                </Button>
              </>
            )}
            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="inline-block text-primary border-b border-current/15 hover:border-current/40">
                На страницу входа
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
